import React, { useRef, useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ArrowBack } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import TemplateTree from "../components/TemplateTree";
import DocumentProcessor from "../components/DocumentProcessor";
import { useTranslation } from "../hooks/useTranslation";
import usePermissionRedirect from "../hooks/usePermissionRedirect";
import { useAuth } from "../context/ApiKeyContext";
import {
  REPLICATE_PROCESSAMENTO_STORAGE_KEY,
  fetchProcessamentoInputPdfBlob,
} from "../utils/replicateProcessamentoJob";
import { normalizeExtractionMode } from "../constants/extractionModes";

const EditorTemplates = () => {
  const fileInputRef = useRef(null);
  const templateTreeRef = useRef(null);
  const [currentTemplateData, setCurrentTemplateData] = useState(null);
  const [replicateJobName, setReplicateJobName] = useState(null);
  const [replicatePdfFile, setReplicatePdfFile] = useState(null);
  const [replicateExtractionMode, setReplicateExtractionMode] =
    useState(null);
  const [replicateLoadError, setReplicateLoadError] = useState(null);
  const history = useHistory();
  const { t } = useTranslation("editor");
  const { t: tJobs } = useTranslation("jobs");
  const { token } = useAuth();

  // Verificar permissão para acessar a página
  usePermissionRedirect("PROCESS_DIGITAL_PDF", "exec_process");

  useEffect(() => {
    let cancelled = false;
    const raw = sessionStorage.getItem(REPLICATE_PROCESSAMENTO_STORAGE_KEY);
    if (!raw || !token) {
      return undefined;
    }

    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(REPLICATE_PROCESSAMENTO_STORAGE_KEY);
      return undefined;
    }

    if (
      !data?.input_payload ||
      !data?.document?.token ||
      typeof data.input_payload !== "object"
    ) {
      sessionStorage.removeItem(REPLICATE_PROCESSAMENTO_STORAGE_KEY);
      return undefined;
    }

    const apply = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      if (cancelled) {
        return;
      }
      if (templateTreeRef.current) {
        templateTreeRef.current.importJson(data.input_payload);
      }
      if (cancelled) {
        return;
      }

      const baseName = (data.job_name || "").trim();
      const suggestedName = baseName
        ? `${baseName} ${tJobs("actions.replicateJobNameSuffix")}`
        : tJobs("actions.replicateDefaultJobName");
      setReplicateJobName(suggestedName);
      /* Normaliza incondicionalmente: um payload gravado antes deste
         parâmetro existir não tem a chave, e cair no default da tela
         (Next-Set26) recriaria em outro pipeline um job que rodou em
         Standard. Ausente resolve para "default", que é o modo real
         desses jobs. */
      setReplicateExtractionMode(
        normalizeExtractionMode(data.extraction_mode),
      );

      try {
        const blob = await fetchProcessamentoInputPdfBlob(
          data.document.token,
          token,
        );
        if (cancelled) {
          return;
        }
        const file = new File(
          [blob],
          data.document.filename || "documento.pdf",
          {
            type: blob.type && blob.type !== "application/octet-stream"
              ? blob.type
              : "application/pdf",
          },
        );
        setReplicatePdfFile(file);
        setReplicateLoadError(null);
      } catch (e) {
        console.error("Réplica: falha ao baixar PDF de entrada", e);
        if (!cancelled) {
          setReplicateLoadError(tJobs("actions.replicatePdfLoadError"));
        }
      } finally {
        if (!cancelled) {
          sessionStorage.removeItem(REPLICATE_PROCESSAMENTO_STORAGE_KEY);
        }
      }
    };

    apply();
    return () => {
      cancelled = true;
    };
    // Réplica: depende do token; textos via tJobs na montagem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        if (templateTreeRef.current) {
          templateTreeRef.current.importJson(json);
        }
      } catch (error) {
        alert(t("importError"));
        console.error("Erro de importação:", error);
      }
    };
    reader.readAsText(file);

    // Limpar o valor do input para permitir selecionar o mesmo arquivo novamente
    event.target.value = null;
  };

  const handleExportClick = () => {
    if (templateTreeRef.current) {
      const json = templateTreeRef.current.exportJson();
      setCurrentTemplateData(json);

      // Criar um blob com o JSON formatado
      const blob = new Blob([JSON.stringify(json, null, 2)], {
        type: "application/json",
      });

      // Criar uma URL para o blob
      const url = URL.createObjectURL(blob);

      // Criar um link para download e clicar nele
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_ikaro.json";
      document.body.appendChild(a);
      a.click();

      // Limpar recursos
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  };

  return (
    <>
      <Paper elevation={3} className="p-24 mb-20">
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          className="mb-16"
        >
          <Grid item>
            <Typography variant="h5" className="font-bold">
              {t("title")}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => history.push("/processamentos")}
              color="primary"
            >
              {t("backToProcessings")}
            </Button>
          </Grid>
        </Grid>
        <Divider className="mb-16" />
        <Typography paragraph>{t("description")}</Typography>
      </Paper>

      {replicateLoadError && (
        <Alert
          severity="warning"
          className="mb-16"
          onClose={() => setReplicateLoadError(null)}
        >
          {replicateLoadError}
        </Alert>
      )}

      <DocumentProcessor
        templateData={currentTemplateData}
        initialJobName={replicateJobName}
        initialPdfFile={replicatePdfFile}
        initialExtractionMode={replicateExtractionMode}
      />

      <Paper elevation={3} className="p-24 mt-20">
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          className="mb-16"
        >
          <Grid item>
            <Typography variant="h6" className="font-bold">
              {t("dataStructure")}
            </Typography>
          </Grid>
          <Grid item>
            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              onChange={handleFileImport}
              className="hidden"
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={handleImportClick}
              className="mr-8"
            >
              {t("importJson")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExportClick}
            >
              {t("exportJson")}
            </Button>
          </Grid>
        </Grid>

        <Divider className="mb-16" />

        <TemplateTree
          ref={templateTreeRef}
          onDataChange={(data) => setCurrentTemplateData(data)}
        />
      </Paper>
    </>
  );
};

export default EditorTemplates;
